// Waiting (02-building-blocks.md): every load goes loading → "taking too long" after 25 s →
// failed in plain words with Retry and a reference; every action is busy until it answers, and
// after 40 s without an answer it is usable again with "No answer yet …". Nothing spins for ever.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type DependencyList, type RefObject } from 'react';
import { describe, type Context, type Failure } from '../core/errors';
import { Button } from './Button';
import { t } from '../i18n';

export const SLOW_MS = 25_000;
export const NO_ANSWER_MS = 40_000;
export const AGAIN_MS = 10_000;

const WIDTHS = [62, 44, 78, 54, 70, 38];
/** Motion is on: the browser animates and the system doesn't ask for less (01, Motion). */
export const moving = () => !!document.body.animate && !matchMedia('(prefers-reduced-motion: reduce)').matches;
/** The sheen's phase from the page's clock, so every placeholder's band crosses in step (02, Bloom 2). */
export const inStep = () => ({ '--sheen-at': `${-Math.round(performance.now() % 1500)}ms` }) as CSSProperties;
/** The `i`th placeholder's length between `min` and `max`, in seven steps: never two neighbours alike (02, Opening in design A). */
export const varied = (i: number, min: number, max: number) => Math.round(min + ((max - min) * ((i * 5 + 2) % 7)) / 6);
/** Placeholders rising in one after another (02, Opening in design A): 8 px over 0.5 s, `gap` ms apart; with reduce motion simply there. */
export function riseIn(all: Iterable<Element>, gap = 40) {
  if (moving()) [...all].forEach((e, i) => e.animate({ opacity: [0, 1], translate: ['0 8px', '0 0'] }, { duration: 500, delay: i * gap, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'backwards' }));
}
/**
 * Skeleton rows (02, Loading, v1.58.24; One loading language, v1.58.25): per row a picture shape
 * (round for people) and `bars` text bars of varied length (with three, a name bar and one or two
 * text bars), at most 12, in the list's own row (`className`, sized by its CSS), fully opaque,
 * hidden from screen readers. The list sets `aria-busy` while they show; content replaces them in
 * one frame. Spinners stay inside buttons and fields. Bloom (v1.58.28): tinted with the circle's
 * colour (or `tone`'s per row), the sheen in step.
 */
export function Skeletons({ count = 12, round, bars = 1, className = 'skeleton-row', as: Row = 'div', tone }: { count?: number; round?: boolean; bars?: 1 | 2 | 3; className?: string; as?: 'div' | 'li'; tone?: (row: number) => CSSProperties | undefined }) {
  const [step] = useState(inStep);
  return Array.from({ length: Math.max(0, Math.min(count, 12)) }, (_, i) => (
    <Row key={i} className={className} data-desyr-skeleton="" aria-hidden="true" style={{ ...step, ...tone?.(i) }}>
      <i className={round ? 'bone round' : 'bone'} />
      <span>
        {Array.from({ length: bars === 3 ? 3 - (i % 2) : bars }, (_, b) => (
          <b key={b} className="bone" style={{ width: `${bars === 3 && b === 0 ? 22 + (i % 3) * 6 : WIDTHS[(i + b * 2) % 6]}%` }} />
        ))}
      </span>
    </Row>
  ));
}

/**
 * Bloom's rise (02, Bloom 2 and 4): when the placeholders in `box` give way, the rows (`rows`) that
 * weren't there come up 8 px and fade in over 260 ms, `gap` ms apart, the first `max` from the top
 * (from the bottom up with `fromEnd`); the others, and all with reduce motion, appear in place.
 * `now`: placeholders (the start's skeleton) gave way to this list as it came: its rows rise at once.
 */
export function useRise(waiting: boolean, box: RefObject<HTMLElement | null>, rows: string, { max = 6, gap = 40, fromEnd = false, now = false } = {}) {
  const before = useRef<Set<Element>>(now ? new Set() : undefined);
  useLayoutEffect(() => {
    const all = [...(box.current?.querySelectorAll(rows) ?? [])];
    if (waiting) return void (before.current = new Set(all));
    const seen = before.current;
    before.current = undefined;
    if (!seen || !moving()) return;
    const fresh = all.filter((row) => !seen.has(row));
    (fromEnd ? fresh.reverse() : fresh).slice(0, max).forEach((row, i) => row.animate({ opacity: [0, 1], translate: ['0 8px', '0 0'] }, { duration: 260, delay: i * gap, easing: 'ease-out', fill: 'backwards' }));
  }, [waiting]);
}

export function Spinner({ size = 24, label }: { size?: number; label?: string }) {
  return (
    <span
      className="spinner"
      style={{ width: size, height: size }}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}

export type Load<T> =
  | { state: 'loading' }
  | { state: 'slow' }
  | { state: 'done'; value: T }
  | { state: 'failed'; failure: Failure };

/** Runs `load` now and on Retry; still waiting after 10 s it asks once more, the first answer counting (02, Loading, v1.58.31); an answer after "too slow" still replaces the message. */
export function useLoad<T>(load: () => Promise<T>, deps: DependencyList, context: Context = 'load') {
  const [status, setStatus] = useState<Load<T>>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let open = true;
    setStatus({ state: 'loading' });
    const answer = (s: Load<T>) => {
      if (!open) return;
      open = false;
      clearTimeout(slow);
      clearTimeout(again);
      setStatus(s);
    };
    const ask = () => load().then((value) => answer({ state: 'done', value }), (error) => answer({ state: 'failed', failure: describe(error, context) }));
    const slow = setTimeout(() => open && setStatus({ state: 'slow' }), SLOW_MS);
    const again = setTimeout(ask, AGAIN_MS);
    ask();
    return () => {
      open = false;
      clearTimeout(slow);
      clearTimeout(again);
    };
  }, [...deps, attempt]);
  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return [status, retry] as const;
}

/** "Reference: NF7K2Q", selectable in one click. */
export function Reference({ code }: { code: string }) {
  return (
    <p className="reference">
      {t('wait.reference', { code: '' })}
      <span data-desyr-error-ref={code}>{code}</span>
    </p>
  );
}

interface StateProps {
  status: Load<unknown>;
  /** Without it the button is Reload and reloads the page. */
  retry?: () => void;
  /** The area's own sentence for "too slow". */
  slowText?: string;
  /** One line, for small places (the end of a list, a dialog). */
  compact?: boolean;
  /** Grey placeholder rows where the content's shape is known. */
  placeholder?: React.ReactNode;
}

/** The loading, too slow and failed states; renders nothing once loaded. */
export function LoadState({ status, retry, slowText = t('wait.tooSlow'), compact, placeholder }: StateProps) {
  if (status.state === 'done') return null;
  if (status.state === 'loading')
    return placeholder ?? <div className="load-wait"><Spinner size={compact ? 24 : 32} /></div>;
  const again = retry ? (
    <Button small={compact} onClick={retry}>{t('common.retry')}</Button>
  ) : (
    <Button small={compact} onClick={() => location.reload()}>{t('common.reload')}</Button>
  );
  const failed = status.state === 'failed';
  return (
    <div className={compact ? 'cant-load compact' : 'cant-load'} role="alert" data-desyr-cant-load="">
      <p>{failed ? status.failure.text : slowText}</p>
      {again}
      {failed && status.failure.reference && <Reference code={status.failure.reference} />}
    </div>
  );
}

/**
 * An action: busy until its answer, usable again after 40 s with "No answer yet …". Only the
 * latest try counts. `run` resolves true when it worked.
 */
export function useAction<A extends unknown[]>(action: (...args: A) => Promise<unknown>, context: Context = 'general') {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure>();
  const [noAnswer, setNoAnswer] = useState(false);
  const latest = useRef(0);
  const run = useCallback(
    async (...args: A): Promise<boolean> => {
      const mine = ++latest.current;
      const current = () => mine === latest.current;
      setBusy(true);
      setFailure(undefined);
      setNoAnswer(false);
      const timer = setTimeout(() => {
        if (!current()) return;
        setBusy(false);
        setNoAnswer(true);
      }, NO_ANSWER_MS);
      try {
        await action(...args);
        if (current()) setNoAnswer(false);
        return current();
      } catch (error) {
        if (current()) {
          setNoAnswer(false);
          setFailure(describe(error, context));
        }
        return false;
      } finally {
        clearTimeout(timer);
        if (current()) setBusy(false);
      }
    },
    [action, context],
  );
  /** The line to show under the action: the failure's words, or "No answer yet …". */
  const message = failure?.text ?? (noAnswer ? t('wait.noAnswer') : undefined);
  return { run, busy, failure, noAnswer, message };
}
